import { get, isNil } from 'lodash'
import { useMemo } from 'react'

import { hasPolicyStarted } from '../../domain'
import {
  InputValueCondition,
  PolicyStartedCondition,
  ProductCondition,
} from '../../state/configuration'
import { useMaybePolicy } from './policy/policyContext'

export const isInputCondition = (condition: unknown): condition is InputValueCondition =>
  Object.prototype.hasOwnProperty.call(condition, 'isDefined')

const useInputCondition = (formData: Record<string, unknown>) => {
  return useMemo(
    () => (condition: InputValueCondition) =>
      condition.isDefined === !isNil(get(formData, condition.name)),
    [formData],
  )
}

const isPolicyStartedCondition = (
  condition: unknown,
): condition is PolicyStartedCondition =>
  Object.prototype.hasOwnProperty.call(condition, 'hasPolicyStarted')

const usePolicyStartedCondition = () => {
  const policy = useMaybePolicy()

  return useMemo(
    () => (condition: PolicyStartedCondition) => {
      return condition.hasPolicyStarted === (policy && hasPolicyStarted(policy))
    },
    [policy],
  )
}

export const useProductConditions = (data: Record<string, unknown> = {}) => {
  const pathDefinedCondition = useInputCondition(data)
  const policyStartedCondition = usePolicyStartedCondition()

  return useMemo(
    () => (it: ProductCondition) => {
      return isInputCondition(it)
        ? pathDefinedCondition(it)
        : isPolicyStartedCondition(it)
        ? policyStartedCondition(it)
        : false
    },
    [pathDefinedCondition, policyStartedCondition],
  )
}
